import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useLocation } from '@tanstack/react-router';
import { EMPLOYMENT_TYPE_LABELS } from '@wodi/domain';
import type { StaffListItemView } from '@wodi/shared';
import {
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
} from '@wodi/ui';
import type { ReactNode } from 'react';

import { can, sessionQuery } from '../api/session.ts';
import { licencesToRenewQuery, staffListQuery } from '../api/staff.ts';
import { useOnline } from '../hooks/use-online.ts';
import { screen, TableScroll } from '../layout/screen.tsx';
import { councilText, dayText, LoadFailed, StaffLicenceTag } from './format.tsx';
import styles from './staff.module.css';

// Staff (docs/plans/phase2-staff-records.md §6): everyone with an account, their licence in
// words and their employment details, and the licences to renew. Opening either list is
// audited on the server.

function StaffTabs() {
  const location = useLocation();
  const links = [
    { to: '/staff', label: 'All staff' },
    { to: '/staff/licences', label: 'Licences to renew' },
  ] as const;
  return (
    <nav aria-label="Staff views" className={screen.tabs}>
      {links.map((l) => (
        <Link
          key={l.to}
          to={l.to}
          activeOptions={{ exact: true }}
          {...(location.pathname === l.to ? { 'aria-current': 'page' as const } : {})}
        >
          {l.label}
        </Link>
      ))}
    </nav>
  );
}

function Frame({ children }: { children: ReactNode }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  if (session === null || !can(session, 'staff.read'))
    return (
      <>
        <PageHeader title="Staff" />
        <Notice tone="info">
          You don&apos;t have permission to see staff records. Ask an administrator if you need it.
        </Notice>
      </>
    );
  return (
    <div className={styles.page}>
      <PageHeader
        title="Staff"
        description="Licences and employment details. Licence details are kept in Settings, Practitioners."
      />
      <div className={styles.toolbar}>
        <StaffTabs />
      </div>
      {online ? null : (
        <Notice tone="info">
          You&apos;re offline. The list shows what was last loaded; it updates when you&apos;re back
          online.
        </Notice>
      )}
      {children}
    </div>
  );
}

function StaffTable({
  label,
  items,
  empty,
}: {
  label: string;
  items: StaffListItemView[];
  empty: string;
}) {
  return (
    <TableScroll>
      <Table flat aria-label={label}>
        <TableHeader>
          <Column isRowHeader>Name</Column>
          <Column>Cadre</Column>
          <Column>Council</Column>
          <Column>Licence number</Column>
          <Column>Expires</Column>
          <Column>Licence</Column>
          <Column>Department</Column>
          <Column>Employment</Column>
        </TableHeader>
        <TableBody
          items={items.map((i) => ({ ...i, id: i.userId }))}
          renderEmptyState={() => empty}
        >
          {(s) => (
            <Row id={s.userId}>
              <Cell>
                <Link to="/staff/$userId" params={{ userId: s.userId }}>
                  {s.displayName}
                </Link>
                {s.userStatus === 'disabled' ? (
                  <span className={styles.meta}> (account disabled)</span>
                ) : null}
              </Cell>
              <Cell>{s.cadre?.display ?? 'Not recorded'}</Cell>
              <Cell>{councilText(s.licenceBody)}</Cell>
              <Cell>
                {s.licenceNumber === null ? (
                  'Not recorded'
                ) : (
                  <span className="wodi-id">{s.licenceNumber}</span>
                )}
              </Cell>
              <Cell>{dayText(s.licenceExpiresOn)}</Cell>
              <Cell>
                <StaffLicenceTag licence={s.licence} />
              </Cell>
              <Cell>{s.employment?.department ?? 'Not recorded'}</Cell>
              <Cell>
                {s.employment === null
                  ? 'Not recorded'
                  : EMPLOYMENT_TYPE_LABELS[s.employment.employmentType]}
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

export function StaffPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'staff.read');
  const staff = useQuery({ ...staffListQuery, enabled: allowed });
  return (
    <Frame>
      <Section title="All staff" description="Everyone with a Wodi account, by name.">
        {staff.isPending && allowed ? <LoadingBlock label="Loading staff" rows={5} /> : null}
        {staff.isError ? (
          <LoadFailed
            error={staff.error}
            fallback="Staff couldn't be loaded. Try again."
            onRetry={() => void staff.refetch()}
          />
        ) : null}
        {staff.isSuccess ? (
          <StaffTable
            label="Staff"
            items={staff.data.items}
            empty="No staff yet. Add people in Settings, Users."
          />
        ) : null}
      </Section>
    </Frame>
  );
}

export function LicencesToRenewPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'staff.read');
  const licences = useQuery({ ...licencesToRenewQuery, enabled: allowed });
  const days = licences.data?.licenceWarningDays;
  return (
    <Frame>
      <Section
        title="Licences to renew"
        description={
          days === undefined
            ? 'Licences that have expired or expire soon.'
            : `Licences that have expired, or expire within ${String(days)} days, soonest first.`
        }
      >
        {licences.isPending && allowed ? <LoadingBlock label="Loading licences" rows={4} /> : null}
        {licences.isError ? (
          <LoadFailed
            error={licences.error}
            fallback="Licences couldn't be loaded. Try again."
            onRetry={() => void licences.refetch()}
          />
        ) : null}
        {licences.isSuccess ? (
          <StaffTable
            label="Licences to renew"
            items={licences.data.items}
            empty={`No licences have expired or expire within ${String(licences.data.licenceWarningDays)} days.`}
          />
        ) : null}
        <p className={styles.meta}>
          The number of days is set in Settings, Practitioners, under &ldquo;When licences show as
          expiring&rdquo;.
        </p>
      </Section>
    </Frame>
  );
}
